import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Checkbox } from '@lifeomic/chroma-react/components/Checkbox';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';
import { useMutation } from '@tanstack/react-query';
import { client } from '../../util/api';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import {
  canProceedToContactConfirm,
  canSubmitContactEdit,
  formatMemberPhoneForDisplay,
  getRawContactValue,
  isValidMemberPhone,
  memberOpsRequestErrorMessage,
  type MemberContactField,
} from './memberContactEdit';

const messages = defineMessages({
  editEmailTitle: {
    id: 'MemberContactEditModal.editEmailTitle',
    defaultMessage: 'Edit email',
  },
  editPhoneTitle: {
    id: 'MemberContactEditModal.editPhoneTitle',
    defaultMessage: 'Edit phone',
  },
  newValueLabel: {
    id: 'MemberContactEditModal.newValueLabel',
    defaultMessage: 'New value',
  },
  confirmValueLabel: {
    id: 'MemberContactEditModal.confirmValueLabel',
    defaultMessage: 'Confirm new value',
  },
  mismatchError: {
    id: 'MemberContactEditModal.mismatchError',
    defaultMessage: 'Both entries must match exactly.',
  },
  phoneInvalidError: {
    id: 'MemberContactEditModal.phoneInvalidError',
    defaultMessage:
      'Enter a valid phone number (include country code for numbers outside the US).',
  },
  confirmationIntro: {
    id: 'MemberContactEditModal.confirmationIntro',
    defaultMessage:
      "Please confirm: you are changing {name}'s {fieldLabel} from {oldValue} to {newValue}. This updates the member's Fountain Life app login and their health record contact. The Elation chart MUST be updated to match the new value.",
  },
  confirmCheckbox: {
    id: 'MemberContactEditModal.confirmCheckbox',
    defaultMessage: 'I confirm the above stated changes.',
  },
  cancel: {
    id: 'MemberContactEditModal.cancel',
    defaultMessage: 'Cancel',
  },
  continue: {
    id: 'MemberContactEditModal.continue',
    defaultMessage: 'Continue',
  },
  back: {
    id: 'MemberContactEditModal.back',
    defaultMessage: 'Back',
  },
  save: {
    id: 'MemberContactEditModal.save',
    defaultMessage: 'Save',
  },
  saveSuccess: {
    id: 'MemberContactEditModal.saveSuccess',
    defaultMessage: 'Contact information updated.',
  },
  saveError: {
    id: 'MemberContactEditModal.saveError',
    defaultMessage: 'Unable to save. Try again.',
  },
  fieldEmail: {
    id: 'MemberContactEditModal.fieldEmail',
    defaultMessage: 'email',
  },
  fieldPhone: {
    id: 'MemberContactEditModal.fieldPhone',
    defaultMessage: 'phone',
  },
  emptyPlaceholder: {
    id: 'MemberContactEditModal.emptyPlaceholder',
    defaultMessage: '(none)',
  },
});

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxWidth: 520,
  },
  actions: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  confirmationBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
}));

export type MemberContactEditModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  field: MemberContactField | null;
  memberName: string;
  patient: WithPlatformDefaults<fhir3.Patient> | null;
  onSaved: () => void | Promise<void>;
};

export const MemberContactEditModal: React.FC<MemberContactEditModalProps> = ({
  isOpen,
  onDismiss,
  field,
  memberName,
  patient,
  onSaved,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const [notify] = useNotify();
  const [first, setFirst] = React.useState('');
  const [second, setSecond] = React.useState('');
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);

  const oldValue = patient && field ? getRawContactValue(patient, field) : '';

  React.useEffect(() => {
    if (!isOpen) {
      setFirst('');
      setSecond('');
      setShowConfirmation(false);
      setConfirmed(false);
    }
  }, [isOpen, field, patient?.id]);

  const onFieldChange = (nextFirst: string, nextSecond: string) => {
    if (showConfirmation) {
      setShowConfirmation(false);
      setConfirmed(false);
    }
    setFirst(nextFirst);
    setSecond(nextSecond);
  };

  const fieldLabel =
    field === 'phone'
      ? formatMessage(messages.fieldPhone)
      : formatMessage(messages.fieldEmail);

  const oldDisplay =
    oldValue.trim() === ''
      ? formatMessage(messages.emptyPlaceholder)
      : oldValue;

  const newTrim = first.trim();
  const newDisplay =
    newTrim === ''
      ? formatMessage(messages.emptyPlaceholder)
      : field === 'phone'
      ? formatMemberPhoneForDisplay(newTrim)
      : newTrim;

  const mismatch =
    first.length > 0 && second.length > 0 && first.trim() !== second.trim();

  const phoneErrorFirst =
    field === 'phone' && newTrim.length > 0 && !isValidMemberPhone(newTrim);
  const phoneErrorSecond =
    field === 'phone' &&
    second.trim().length > 0 &&
    !isValidMemberPhone(second);

  const canContinue =
    patient &&
    field &&
    canProceedToContactConfirm({ first, second, oldValue, field });

  const canSave =
    showConfirmation &&
    patient &&
    field &&
    canSubmitContactEdit({
      first,
      second,
      oldValue,
      confirmed,
      field,
    });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!patient || !field) throw new Error('missing patient or field');
      const trimmed = first.trim();
      if (field === 'email') {
        await client.post('/v1/member-operations/members/change-email', {
          patientId: patient.id,
          newEmail: trimmed,
        });
      } else {
        await client.post('/v1/member-operations/members/change-phone', {
          patientId: patient.id,
          newPhoneNumber: trimmed,
        });
      }
    },
    onSuccess: async () => {
      notify(formatMessage(messages.saveSuccess), 'success');
      await Promise.resolve(onSaved());
      onDismiss();
    },
    onError: (err: unknown) => {
      notify(
        memberOpsRequestErrorMessage(err) ?? formatMessage(messages.saveError),
        'error',
      );
    },
  });

  if (!field || !patient) {
    return null;
  }

  const title =
    field === 'phone'
      ? formatMessage(messages.editPhoneTitle)
      : formatMessage(messages.editEmailTitle);

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={title}
      data-testid="member-contact-edit-modal"
    >
      <Box className={classes.body}>
        {showConfirmation ? null : (
          <>
            <TextField
              label={formatMessage(messages.newValueLabel)}
              value={first}
              onChange={e => onFieldChange(e.target.value, second)}
              fullWidth
              hasError={phoneErrorFirst}
              errorMessage={
                phoneErrorFirst
                  ? formatMessage(messages.phoneInvalidError)
                  : undefined
              }
              type={field === 'phone' ? 'tel' : undefined}
              data-testid="member-contact-edit-first"
            />
            <TextField
              label={formatMessage(messages.confirmValueLabel)}
              value={second}
              onChange={e => onFieldChange(first, e.target.value)}
              fullWidth
              hasError={mismatch || phoneErrorSecond}
              errorMessage={
                mismatch
                  ? formatMessage(messages.mismatchError)
                  : phoneErrorSecond
                  ? formatMessage(messages.phoneInvalidError)
                  : undefined
              }
              type={field === 'phone' ? 'tel' : undefined}
              data-testid="member-contact-edit-second"
            />
          </>
        )}

        {showConfirmation ? (
          <Box className={classes.confirmationBlock}>
            <Text size="subbody">
              {formatMessage(messages.confirmationIntro, {
                name: memberName,
                fieldLabel,
                oldValue: oldDisplay,
                newValue: newDisplay,
              })}
            </Text>
            <Checkbox
              label={formatMessage(messages.confirmCheckbox)}
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              data-testid="member-contact-edit-confirm"
            />
          </Box>
        ) : null}

        <Box className={classes.actions}>
          <Box>
            {showConfirmation ? (
              <Button
                variant="outlined"
                onClick={() => {
                  setShowConfirmation(false);
                  setConfirmed(false);
                }}
                data-testid="member-contact-edit-back"
              >
                {formatMessage(messages.back)}
              </Button>
            ) : null}
          </Box>
          <Box className={classes.actionButtons}>
            <Button variant="outlined" onClick={onDismiss}>
              {formatMessage(messages.cancel)}
            </Button>
            {showConfirmation ? (
              <Button
                variant="contained"
                disabled={!canSave || saveMutation.isLoading}
                onClick={() => saveMutation.mutate()}
                data-testid="member-contact-edit-save"
              >
                {formatMessage(messages.save)}
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={!canContinue}
                onClick={() => setShowConfirmation(true)}
                data-testid="member-contact-edit-continue"
              >
                {formatMessage(messages.continue)}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
